import { useEffect, useState } from "react";
import { adminApi, extractApiError } from "../../lib/adminApi";
import {
    AdminPageHeader,
    SectionCard,
    FormActions,
} from "../../components/admin/FormPrimitives";
import { BilingualField } from "../../components/admin/BilingualField";

function useSection(section) {
    const [data, setData] = useState(null);
    const [state, setState] = useState({ status: "idle", error: "" });

    useEffect(() => {
        (async () => {
            try {
                const { data: doc } = await adminApi.get(
                    `/admin/site-content/${section}`,
                );
                setData(doc.data || {});
            } catch (err) {
                if (err?.response?.status === 404) {
                    setData({}); // section not yet created — PUT will upsert
                } else {
                    setState({
                        status: "error",
                        error: extractApiError(err),
                    });
                }
            }
        })();
    }, [section]);

    const save = async (payload) => {
        setState({ status: "submitting", error: "" });
        try {
            await adminApi.put(`/admin/site-content/${section}`, {
                data: payload,
            });
            setState({ status: "success", error: "" });
            setTimeout(
                () => setState((s) => (s.status === "success" ? { status: "idle", error: "" } : s)),
                2500,
            );
        } catch (err) {
            setState({ status: "error", error: extractApiError(err) });
        }
    };

    return { data, setData, save, state };
}

/* ---------------- HOME ---------------- */
function HomeEditor() {
    const { data, setData, save, state } = useSection("home");
    if (data === null) return <SectionCard title="Accueil">Chargement…</SectionCard>;
    return (
        <SectionCard
            title="Accueil — Hero et sections"
            description="Titre principal, accroche, bandeau de crédibilité et la section 'Pourquoi MABS'."
            testId="content-home"
        >
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    save(data);
                }}
            >
                <BilingualField
                    label="Titre principal (H1)"
                    idPrefix="home-hero-title"
                    value={data.hero_title}
                    onChange={(v) => setData({ ...data, hero_title: v })}
                />
                <BilingualField
                    label="Accroche (tagline)"
                    idPrefix="home-hero-tagline"
                    value={data.hero_tagline}
                    onChange={(v) => setData({ ...data, hero_tagline: v })}
                />
                <BilingualField
                    label="Sous-titre"
                    idPrefix="home-hero-sub"
                    type="textarea"
                    rows={2}
                    value={data.hero_subtitle}
                    onChange={(v) => setData({ ...data, hero_subtitle: v })}
                />
                <BilingualField
                    label="Bandeau de crédibilité"
                    idPrefix="home-credibility"
                    type="textarea"
                    rows={3}
                    value={data.credibility_banner}
                    onChange={(v) =>
                        setData({ ...data, credibility_banner: v })
                    }
                />
                <div className="eyebrow mt-8 mb-3">Pourquoi le cabinet MABS (5 items)</div>
                {(data.why_mabs || []).map((item, i) => (
                    <div key={i} className="mb-6 border-l-2 border-gold/40 pl-5">
                        <BilingualField
                            label={`Item ${i + 1} — Titre`}
                            idPrefix={`why-title-${i}`}
                            value={item.title}
                            onChange={(v) => {
                                const arr = [...data.why_mabs];
                                arr[i] = { ...arr[i], title: v };
                                setData({ ...data, why_mabs: arr });
                            }}
                        />
                        <BilingualField
                            label="Texte"
                            type="textarea"
                            rows={2}
                            idPrefix={`why-text-${i}`}
                            value={item.text}
                            onChange={(v) => {
                                const arr = [...data.why_mabs];
                                arr[i] = { ...arr[i], text: v };
                                setData({ ...data, why_mabs: arr });
                            }}
                        />
                    </div>
                ))}
                <FormActions
                    saving={state.status === "submitting"}
                    status={state.status}
                    error={state.error}
                    testId="content-home-form"
                />
            </form>
        </SectionCard>
    );
}

/* ---------------- ABOUT ---------------- */
function AboutEditor() {
    const { data, setData, save, state } = useSection("about");
    if (data === null) return <SectionCard title="À propos">Chargement…</SectionCard>;
    return (
        <SectionCard
            title="À propos — Bio et valeurs"
            testId="content-about"
        >
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    save(data);
                }}
            >
                <BilingualField
                    label="Titre de la page"
                    idPrefix="about-title"
                    value={data.title}
                    onChange={(v) => setData({ ...data, title: v })}
                />
                <BilingualField
                    label="Biographie complète (paragraphes séparés par une ligne vide)"
                    type="textarea"
                    rows={14}
                    idPrefix="about-bio"
                    value={data.bio}
                    onChange={(v) => setData({ ...data, bio: v })}
                />
                <div className="eyebrow mt-8 mb-3">Valeurs (4 items)</div>
                {(data.values || []).map((item, i) => (
                    <div key={i} className="mb-6 border-l-2 border-gold/40 pl-5">
                        <BilingualField
                            label={`Valeur ${i + 1} — Titre`}
                            idPrefix={`about-value-title-${i}`}
                            value={item.title}
                            onChange={(v) => {
                                const arr = [...data.values];
                                arr[i] = { ...arr[i], title: v };
                                setData({ ...data, values: arr });
                            }}
                        />
                        <BilingualField
                            label="Texte"
                            type="textarea"
                            rows={2}
                            idPrefix={`about-value-text-${i}`}
                            value={item.text}
                            onChange={(v) => {
                                const arr = [...data.values];
                                arr[i] = { ...arr[i], text: v };
                                setData({ ...data, values: arr });
                            }}
                        />
                    </div>
                ))}
                <BilingualField
                    label="Note sur le serment"
                    type="textarea"
                    rows={2}
                    idPrefix="about-oath"
                    value={data.oath_note}
                    onChange={(v) => setData({ ...data, oath_note: v })}
                />
                <FormActions
                    saving={state.status === "submitting"}
                    status={state.status}
                    error={state.error}
                    testId="content-about-form"
                />
            </form>
        </SectionCard>
    );
}

/* ---------------- PROCESS ---------------- */
function ProcessEditor() {
    const { data, setData, save, state } = useSection("process");
    if (data === null)
        return <SectionCard title="Fonctionnement">Chargement…</SectionCard>;
    return (
        <SectionCard
            title="Fonctionnement — Première consultation"
            testId="content-process"
        >
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    save(data);
                }}
            >
                <BilingualField
                    label="Titre"
                    idPrefix="process-title"
                    value={data.title}
                    onChange={(v) => setData({ ...data, title: v })}
                />
                <BilingualField
                    label="Introduction"
                    type="textarea"
                    rows={3}
                    idPrefix="process-intro"
                    value={data.intro}
                    onChange={(v) => setData({ ...data, intro: v })}
                />
                <div className="eyebrow mt-8 mb-3">Étapes</div>
                {(data.steps || []).map((s, i) => (
                    <div key={i} className="mb-6 border-l-2 border-gold/40 pl-5">
                        <BilingualField
                            label={`Étape ${i + 1} — Titre`}
                            idPrefix={`process-step-title-${i}`}
                            value={s.title}
                            onChange={(v) => {
                                const arr = [...data.steps];
                                arr[i] = { ...arr[i], title: v };
                                setData({ ...data, steps: arr });
                            }}
                        />
                        <BilingualField
                            label="Texte"
                            type="textarea"
                            rows={3}
                            idPrefix={`process-step-text-${i}`}
                            value={s.text}
                            onChange={(v) => {
                                const arr = [...data.steps];
                                arr[i] = { ...arr[i], text: v };
                                setData({ ...data, steps: arr });
                            }}
                        />
                    </div>
                ))}
                <FormActions
                    saving={state.status === "submitting"}
                    status={state.status}
                    error={state.error}
                    testId="content-process-form"
                />
            </form>
        </SectionCard>
    );
}

/* ---------------- LEGAL ---------------- */
function LegalEditor() {
    const { data, setData, save, state } = useSection("legal_disclaimer");
    if (data === null)
        return <SectionCard title="Mentions légales">Chargement…</SectionCard>;
    return (
        <SectionCard
            title="Mentions légales / avertissement"
            testId="content-legal"
        >
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    save(data);
                }}
            >
                <BilingualField
                    label="Titre"
                    idPrefix="legal-title"
                    value={data.title}
                    onChange={(v) => setData({ ...data, title: v })}
                />
                <BilingualField
                    label="Texte de l'avertissement"
                    type="textarea"
                    rows={6}
                    idPrefix="legal-text"
                    value={data.text}
                    onChange={(v) => setData({ ...data, text: v })}
                />
                <FormActions
                    saving={state.status === "submitting"}
                    status={state.status}
                    error={state.error}
                    testId="content-legal-form"
                />
            </form>
        </SectionCard>
    );
}

/* ---------------- SEO ---------------- */
function SeoEditor() {
    const { data, setData, save, state } = useSection("seo");
    if (data === null) return <SectionCard title="SEO">Chargement…</SectionCard>;
    const pages = ["home", "about", "practice", "process", "blog", "contact"];
    return (
        <SectionCard
            title="Référencement (SEO)"
            description="Titre et description par page et par langue. Utilisés dans les onglets et les moteurs de recherche."
            testId="content-seo"
        >
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    save(data);
                }}
            >
                {pages.map((p) => (
                    <div key={p} className="mb-8">
                        <div className="eyebrow mb-3">Page /{p === "home" ? "" : p}</div>
                        <BilingualField
                            label="Titre (title)"
                            idPrefix={`seo-${p}-title`}
                            value={data[p]?.title}
                            onChange={(v) =>
                                setData({
                                    ...data,
                                    [p]: { ...(data[p] || {}), title: v },
                                })
                            }
                        />
                        <BilingualField
                            label="Description (meta)"
                            type="textarea"
                            rows={2}
                            idPrefix={`seo-${p}-desc`}
                            value={data[p]?.description}
                            onChange={(v) =>
                                setData({
                                    ...data,
                                    [p]: { ...(data[p] || {}), description: v },
                                })
                            }
                        />
                    </div>
                ))}
                <FormActions
                    saving={state.status === "submitting"}
                    status={state.status}
                    error={state.error}
                    testId="content-seo-form"
                />
            </form>
        </SectionCard>
    );
}

export default function AdminContent() {
    return (
        <div data-testid="admin-content-page">
            <AdminPageHeader
                title="Contenus du site"
                subtitle="Chaque champ existe en français et en anglais. Les modifications s'appliquent immédiatement sur le site public."
            />
            <HomeEditor />
            <AboutEditor />
            <ProcessEditor />
            <LegalEditor />
            <SeoEditor />
        </div>
    );
}
